import { rtkApi as api } from '../rtkApi';

export interface Addendum {
  _id: string;
  contractId: string;
  addendumNumber: number;
  title: string;
  description: string;
  type:
    | 'amendment'
    | 'extension'
    | 'attachment'
    | 'repair_request'
    | 'name_change'
    | 'general'
    | 'other';
  content: string;
  templateId?: string;
  templateSnapshot?: {
    title?: string;
    type?: string;
    body?: string;
    placeholders?: string[];
  };
  fields?: Record<string, string>;
  documentHtml?: string;
  documentId?: string;
  createdBy: 'buyer' | 'seller' | 'agent';
  status: 'draft' | 'pending_signatures' | 'signed' | 'rejected';
  extensionDetails?: {
    requestedExtensionDays: number;
    reason: string;
    newDeadline: string;
    affectedMilestones: string[];
  };
  signatures?: Array<{
    signerRole: 'buyer' | 'seller' | 'agent';
    signerName: string;
    signature: string;
    signedAt: string;
  }>;
  deadline?: string;
  approvedBy?: {
    buyer: boolean;
    seller: boolean;
    agent: boolean;
  };
  notes?: Array<{
    addedBy: 'buyer' | 'seller' | 'agent';
    note: string;
    addedAt: string;
  }>;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAddendumRequest {
  contractId: string;
  title: string;
  description: string;
  type:
    | 'amendment'
    | 'extension'
    | 'attachment'
    | 'repair_request'
    | 'name_change'
    | 'general'
    | 'other';
  content?: string;
  templateId?: string;
  fields?: Record<string, string>;
  extensionDetails?: {
    requestedExtensionDays: number;
    reason: string;
    newDeadline: string;
    affectedMilestones: string[];
  };
  createdBy: 'buyer' | 'seller' | 'agent';
}

export interface SignAddendumRequest {
  signature: string;
  signerRole: 'buyer' | 'seller' | 'agent';
  signerName: string;
}

export interface AddendumTemplate {
  _id: string;
  type:
    | 'extension'
    | 'repair_request'
    | 'name_change'
    | 'general'
    | 'amendment'
    | 'attachment'
    | 'other';
  title: string;
  body: string;
  placeholders: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export const addendumApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getAddendums: builder.query<
      { success: boolean; data: Addendum[]; count: number },
      string
    >({
      query: (contractId) => ({
        url: `/addendum/contract/${contractId}`,
        method: 'GET',
      }),
      providesTags: ['Addendum'],
    }),
    getAddendum: builder.query<{ success: boolean; data: Addendum }, string>({
      query: (addendumId) => ({
        url: `/addendum/${addendumId}`,
        method: 'GET',
      }),
      providesTags: ['Addendum'],
    }),
    createAddendum: builder.mutation<
      { success: boolean; data: Addendum; message: string },
      CreateAddendumRequest
    >({
      query: (addendumData) => ({
        url: `/addendum/contract/${addendumData.contractId}/create`,
        method: 'POST',
        body: addendumData,
      }),
      invalidatesTags: ['Addendum', 'Contract'],
    }),
    updateAddendum: builder.mutation<
      { success: boolean; data: Addendum; message: string },
      { addendumId: string; updateData: Partial<Addendum> }
    >({
      query: ({ addendumId, updateData }) => ({
        url: `/addendum/${addendumId}`,
        method: 'PUT',
        body: updateData,
      }),
      invalidatesTags: ['Addendum'],
    }),
    deleteAddendum: builder.mutation<
      { success: boolean; message: string },
      string
    >({
      query: (addendumId) => ({
        url: `/addendum/${addendumId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Addendum'],
    }),
    signAddendum: builder.mutation<
      { success: boolean; data: Addendum; message: string },
      { addendumId: string; signatureData: SignAddendumRequest }
    >({
      query: ({ addendumId, signatureData }) => ({
        url: `/addendum/${addendumId}/sign`,
        method: 'POST',
        body: signatureData,
      }),
      invalidatesTags: ['Addendum'],
    }),
    approveAddendumTerms: builder.mutation<
      { success: boolean; data: Addendum; message: string },
      { addendumId: string; approverRole: 'buyer' | 'seller' | 'agent' }
    >({
      query: ({ addendumId, approverRole }) => ({
        url: `/addendum/${addendumId}/approve-terms`,
        method: 'POST',
        body: { approverRole },
      }),
      invalidatesTags: ['Addendum'],
    }),
    rejectAddendum: builder.mutation<
      { success: boolean; data: Addendum; message: string },
      {
        addendumId: string;
        rejectorRole: 'buyer' | 'seller' | 'agent';
        reason?: string;
      }
    >({
      query: ({ addendumId, rejectorRole, reason }) => ({
        url: `/addendum/${addendumId}/reject`,
        method: 'POST',
        body: { rejectorRole, reason },
      }),
      invalidatesTags: ['Addendum'],
    }),
    addAddendumNote: builder.mutation<
      { success: boolean; data: Addendum; message: string },
      {
        addendumId: string;
        addedBy: 'buyer' | 'seller' | 'agent';
        note: string;
      }
    >({
      query: ({ addendumId, addedBy, note }) => ({
        url: `/addendum/${addendumId}/note`,
        method: 'POST',
        body: { addedBy, note },
      }),
      invalidatesTags: ['Addendum'],
    }),
    getActiveAddendumTemplates: builder.query<AddendumTemplate[], void>({
      query: () => ({
        url: '/addendum/templates',
        method: 'GET',
      }),
      transformResponse: (response: {
        success: boolean;
        data: AddendumTemplate[];
      }) => response.data,
      providesTags: ['AddendumTemplates'],
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetAddendumsQuery,
  useGetAddendumQuery,
  useCreateAddendumMutation,
  useUpdateAddendumMutation,
  useDeleteAddendumMutation,
  useSignAddendumMutation,
  useApproveAddendumTermsMutation,
  useRejectAddendumMutation,
  useAddAddendumNoteMutation,
  useGetActiveAddendumTemplatesQuery,
} = addendumApi;
